import dayjs, { Dayjs } from 'dayjs'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Stack } from '@mui/material'

import DatePickerInput from './DatePickerInput'
import TimePickerInput from './TimePickerInput'

type DayRangePickerProps = {
  readOnly?: boolean
  startDate: Date
  endDate: Date
  onChange: (startDate: Date, endDate: Date) => void
}

export function DayRangePicker(props: DayRangePickerProps) {
  const { readOnly, startDate, endDate, onChange } = props
  const { t } = useTranslation()

  const date = useMemo(() => dayjs(startDate), [startDate])
  const startMinute = useMemo(() => dayjs(startDate), [startDate])
  const endMinute = useMemo(() => dayjs(endDate), [endDate])

  const handleDateChange = (currentDate: Dayjs | null) => {
    if (!currentDate) return
    handleDateRangeChange(currentDate, startMinute, endMinute)
  }

  const handleStartMinuteChange = (startDate: Dayjs | null) => {
    if (!startDate) return
    handleDateRangeChange(date, startDate, endMinute)
  }

  const handleEndMinuteChange = (endDate: Dayjs | null) => {
    if (!endDate) return
    handleDateRangeChange(date, startMinute, endDate)
  }

  const handleDateRangeChange = (currentDate: Dayjs | null, startDate: Dayjs | null, endDate: Dayjs | null) => {
    if (!currentDate || !startDate || !endDate) return

    const newStartDate = currentDate.hour(startDate.hour()).minute(startDate.minute()).toDate()
    const newEndDate = currentDate.hour(endDate.hour()).minute(endDate.minute()).toDate()
    onChange(newStartDate, newEndDate)
  }

  return (
    <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} width="100%">
      <Box flex={2}>
        <DatePickerInput fullWidth value={date} onChange={handleDateChange} label={t('component.ui.dayrangepicker.day')} readOnly={readOnly} />
      </Box>
      <Box flex={1}>
        <TimePickerInput
          fullWidth
          value={startMinute}
          onChange={handleStartMinuteChange}
          label={t('component.ui.dayrangepicker.start')}
          readOnly={readOnly}
        />
      </Box>
      <Box flex={1}>
        <TimePickerInput
          fullWidth
          value={endMinute}
          onChange={handleEndMinuteChange}
          label={t('component.ui.dayrangepicker.end')}
          readOnly={readOnly}
        />
      </Box>
    </Stack>
  )
}
